import {
  Component,
  effect,
  EventEmitter,
  input,
  Input,
  model,
  OnInit,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';
import { TransactionStatusLabel } from '../../../_interfaces/transactions.interface';

export interface FilterGroup {
  filterLabel: string;
  param: string;
  options: FilterOption[];
  type?: FilterGroupType;
  hidden?: boolean;
}
export interface FilterOption {
  label: string;
  value: string | number | boolean;
  type: FilterGroupType;
  name?: string;
  checked: boolean | undefined;
}

export enum FilterGroupType {
  checkbox = 'checkbox',
  radio = 'radio',
}

@Component({
  selector: 'app-filter-button',
  imports: [FormsModule, ClickOutsideDirective, NgClass],
  templateUrl: './filter-button.component.html',
  styleUrl: '../../../../static/styles/components/filter-button.component.scss',
})
export class FilterButtonComponent implements OnInit {
  @Input({ required: true }) filterGroups!: Array<FilterGroup>;
  @Input() hasOtherActiveFilters: boolean = false;
  @Input() filterWide: boolean = false;
  isDepositView = input<boolean>();
  @Output() applyFilter: EventEmitter<Array<FilterOption>> = new EventEmitter<Array<FilterOption>>();
  @Output() requestReset: EventEmitter<boolean> = new EventEmitter<boolean>();
  resetConfirmed = model<boolean>(false);
  protected readonly FilterGroupType = FilterGroupType;
  protected readonly TransactionStatusLabel = TransactionStatusLabel;

  constructor() {
    effect(() => {
      // allow parent to reset outside filters so that we can apply everything at the same time.
      if (this.resetConfirmed()) {
        this.runApplyFilter();
        // Flip the signal back to false!
        // This will automatically update the parent's state too.
        this.resetConfirmed.set(false);
      }

      // Toggle "Receipts" filter visibility based on the deposit view
      if (this.filterGroups && this.filterGroups.length > 0) {
        // Find the "Receipts" filter group by param name
        const receiptsFilterIndex = this.filterGroups.findIndex((group) => group.param === 'receipt');

        if (receiptsFilterIndex !== -1) {
          // Hide when in the deposit view, show when not
          this.filterGroups[receiptsFilterIndex].hidden = this.isDepositView();
          if (this.isDepositView()) {
            this.filterGroups[receiptsFilterIndex].options.forEach((option) => {
              option.checked = false;
            });
          }
        }
        this.updateActiveBadge();
      }
    });
  }

  protected activeFilter: WritableSignal<boolean> = signal<boolean>(false);
  show: boolean = false;

  ngOnInit(): void {
    this.filterGroups = this.filterGroups || [];
    this.updateActiveBadge();
  }

  updateActiveBadge() {
    const hasChecked = this.filterGroups.some((group) => group.options.some((option) => option.checked));
    this.activeFilter.set(hasChecked || this.hasOtherActiveFilters);
  }

  selectOption(groupIndex: number, optionIndex: number, type?: FilterGroupType) {
    if (type === FilterGroupType.radio) {
      this.filterGroups[groupIndex].options.forEach((option, index) => {
        option.checked = index === optionIndex;
      });
    } else {
      this.filterGroups[groupIndex].options[optionIndex].checked =
        !this.filterGroups[groupIndex].options[optionIndex].checked;
    }
  }

  onApplyFilter() {
    this.runApplyFilter();
    this.closePopup();
  }

  runApplyFilter() {
    this.applyFilter.emit(this.filterGroups.flatMap((group) => group.options));
    this.updateActiveBadge();
  }

  onResetGroup(group: FilterGroup) {
    group.options.forEach((option) => {
      option.checked = false; // Reset each option to unchecked
    });
  }

  onReset() {
    this.filterGroups.forEach((group) => {
      group.options.forEach((option) => {
        option.checked = false; // Reset each option to unchecked
      });
    });
    if (this.hasOtherActiveFilters) {
      this.requestReset.emit(true); // Notify parent to clear outside filters
    } else {
      this.runApplyFilter();
    }
  }

  closePopup() {
    this.show = false;
  }
}
